/**
 * An article's words as they are stored and drawn (piece 30, Article Editor).
 *
 * 🔨 The table of contents is computed at render time from the article's own
 * H1 and H2 nodes and never persisted: the stored body carries an empty
 * placeholder where the block sits, and never the heading list itself.
 *
 * 🔒 An article reaches a File through a file link in its words (decision
 * 0755): the link names the File by its id, and the same stored body draws the
 * page and the PDF. Everything else is the article's own allowed table
 * (decision 0800), layered over the shared one, which runs on every part of the
 * body the two markers do not cover.
 */
import { RICH_TEXT_ALIGNMENTS, RICH_TEXT_TAGS, escapeHtml, richTextPlain, sanitizeRichText, type RichTextAllowance } from '../controls/richText';
import { fontPointSizeOf, fontPointValue } from '../modals/richText';
import { ARTICLE_CALLOUT_ATTRIBUTE, articleCalloutKindOf, articleCalloutsKept } from './callouts';

/** The one stored form of the table of contents: an empty marker, never its entries. */
export const TOC_PLACEHOLDER = '<nav data-kb-toc="true"></nav>';

/** The marker as the editor may hand it back: with the entries the editor drew inside it, or other attributes beside it. */
const TOC_ANY = /<nav\b[^>]*\bdata-kb-toc="true"[^>]*>[\s\S]*?<\/nav\s*>/gi;

/** A file link, in whatever order the editor wrote its attributes. */
const FILE_LINK_ANY = /<a\b[^>]*\bdata-kb-file="([0-9a-fA-F-]{36})"[^>]*>([\s\S]*?)<\/a\s*>/gi;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The stored form of a link to one File, its label plain text. */
export function fileLinkHtml(fileId: string, label: string): string {
  if (!UUID.test(fileId)) throw new Error(`${fileId} is not a file id`);
  return `<a data-kb-file="${fileId.toLowerCase()}">${escapeHtml(label.trim() || fileId)}</a>`;
}

/** The label a file link carries, read back as plain text. */
function labelOf(inner: string): string {
  return richTextPlain(inner).replace(/\s+/g, ' ').trim();
}

/**
 * 🔒 The three sizes the article's size menu wrote before decision 1615,
 * relative to the article's own body text. A stored article still carries them,
 * so the sanitizer keeps them; the menu writes the point ladder of
 * `FONT_POINT_SIZES` and none of these any more.
 */
export const ARTICLE_FONT_SIZES = { small: '0.85em', large: '1.3em', huge: '1.7em' } as const;

const STYLED = ['style'] as const;

/**
 * 🔒 Where a picture keeps its caption (T724; decision 1476): an attribute on
 * the picture itself, beside its alt text, empty by default. The reader and the
 * PDF draw it under the picture; it is never copied into the alt text.
 */
export const ARTICLE_PICTURE_CAPTION_ATTRIBUTE = 'data-kb-caption';
/** The longest caption kept, as the alt text keeps its words short. */
export const ARTICLE_PICTURE_CAPTION_MAX = 200;

/** The entities a browser writes into an attribute, and a numbered one, decoded back to the characters typed. */
const CAPTION_ENTITY = /&(?:#(\d{1,7})|#x([0-9a-f]{1,6})|(amp|lt|gt|quot|apos|nbsp));/gi;
const CAPTION_NAMED: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

function captionCharacter(whole: string, decimal?: string, hex?: string, name?: string): string {
  if (name) return CAPTION_NAMED[name.toLowerCase()] ?? whole;
  const code = decimal ? Number(decimal) : Number.parseInt(hex ?? '', 16);
  return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
}

/**
 * 🔨 A picture's words kept as one line of at most 200 characters (decision
 * 1476), counted as typed, for the caption and the alt text alike: the editor
 * writes `&`, `"` and `<` as entities, so the words are decoded before the cut
 * and encoded again after it. A cut counted on the entities landed inside one
 * and stored text such as `&amp;am` (the KB4 review, 2026-10-01; T759 for the
 * alt text). Encoding every `&` keeps a typed `&amp;` as itself. An empty line
 * comes back as an empty string, which the table then drops.
 */
function pictureLineKept(value: string): string {
  const typed = value.replace(CAPTION_ENTITY, captionCharacter).replace(/\s+/g, ' ').trim().slice(0, ARTICLE_PICTURE_CAPTION_MAX).trim();
  return escapeHtml(typed);
}

/**
 * 🔒 The article's allowed table (decision 0800), layered over the shared one:
 * the shared tags, and the third heading, tables, a horizontal rule, a code
 * block, a picture and a styled span besides. A picture keeps its File's id and
 * its words for a reader who cannot see it, and never an address.
 */
export const ARTICLE_RICH_TEXT_TAGS: Readonly<Record<string, readonly string[]>> = {
  ...RICH_TEXT_TAGS,
  h3: STYLED,
  pre: STYLED,
  code: [],
  span: STYLED,
  hr: [],
  table: [],
  thead: [],
  tbody: [],
  tr: [],
  th: STYLED,
  td: STYLED,
  // 🔒 A picture keeps its width, a whole percent of the column, its minimum in pixels (decision 1942) and no other declaration (decision 1242), and its caption (decision 1476).
  img: ['data-kb-file', 'alt', ARTICLE_PICTURE_CAPTION_ATTRIBUTE, 'style'],
  // 🔒 A numbered list keeps the number it opens at and a lettered list its letters (decision 1011).
  ol: ['start', 'type'],
  // 🔒 A callout keeps its kind, one of the three words, and nothing else (T3572; decision 10530).
  aside: [ARTICLE_CALLOUT_ATTRIBUTE],
};

/**
 * 🔒 What writes each element and each declaration the article keeps (decision
 * 0800): a tool of its toolbar, the typing itself, or a paste from elsewhere.
 * The toolbar test reads it both ways, so a kept element nothing draws and a
 * drawn tool whose output is dropped are each a failure.
 */
export const ARTICLE_ELEMENT_WRITERS: Readonly<Record<string, string>> = {
  p: 'typing', br: 'typing', div: 'typing',
  b: 'bold', i: 'italic', u: 'underline', strike: 'strikethrough', strong: 'paste', em: 'paste', s: 'paste',
  ul: 'bulletList', ol: 'numberList', li: 'bulletList',
  h1: 'headingStyle', h2: 'headingStyle', h3: 'headingStyle', h4: 'paste', blockquote: 'headingStyle', pre: 'headingStyle', code: 'paste',
  a: 'link', span: 'fontSize', hr: 'horizontalRule', img: 'image',
  table: 'table', thead: 'tableHeader', tbody: 'table', tr: 'tableRowBelow', th: 'tableHeader', td: 'tableColumnRight',
  'text-align': 'alignCenter', 'font-size': 'fontSize', color: 'textColor', 'background-color': 'highlightColor',
  width: 'picturePercent',
  '--kb-min-width': 'pictureMinWidth',
  [ARTICLE_PICTURE_CAPTION_ATTRIBUTE]: 'pictureCaption',
  // 🔒 The style menu's Note, Warning and Tip write a callout and its kind (decision 10530).
  aside: 'headingStyle',
  [ARTICLE_CALLOUT_ATTRIBUTE]: 'headingStyle',
};

/** The declarations a kept style may carry, in the one order they are written back. */
export const ARTICLE_STYLE_PROPERTIES = ['text-align', 'font-size', 'color', 'background-color'] as const;
export type ArticleStyleProperty = (typeof ARTICLE_STYLE_PROPERTIES)[number];

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/;
const RGB = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([0-9.]+)\s*)?\)$/;

/** A colour the editor wrote, as six lower-case hex digits, or null for anything else: a name, a variable, a translucent wash. */
export function articleColourOf(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  const hex = HEX.exec(value);
  if (hex) {
    const digits = hex[1]!;
    return `#${digits.length === 3 ? digits.split('').map((d) => d + d).join('') : digits}`;
  }
  const rgb = RGB.exec(value);
  if (!rgb) return null;
  if (rgb[4] !== undefined && Number(rgb[4]) !== 1) return null;
  const channels = [rgb[1], rgb[2], rgb[3]].map(Number);
  if (channels.some((channel) => channel > 255)) return null;
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * 🔒 A style read as the article keeps it (decision 0800): the alignment, the
 * size, the colour and the highlight, each with a value this function checked,
 * written back one way. 🔨 Anything else a style carries is dropped, whatever
 * wrote it; a style is never kept as the text it arrived as.
 */
export function articleStyleOf(style: string): string | null {
  const found = new Map<ArticleStyleProperty, string>();
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase() as ArticleStyleProperty;
    const value = declaration.slice(colon + 1).trim().toLowerCase();
    if (found.has(property)) continue;
    if (property === 'text-align' && (RICH_TEXT_ALIGNMENTS as readonly string[]).includes(value)) found.set(property, value);
    if (property === 'font-size' && (Object.values(ARTICLE_FONT_SIZES) as string[]).includes(value)) found.set(property, value);
    // 🔒 A size of the point ladder the menu writes now (decision 1615), beside the three relative sizes an older article carries.
    if (property === 'font-size' && !found.has(property)) {
      const points = fontPointSizeOf(value);
      if (points) found.set(property, fontPointValue(points));
    }
    if (property === 'color' || property === 'background-color') {
      const colour = articleColourOf(value);
      if (colour) found.set(property, colour);
    }
  }
  const kept = ARTICLE_STYLE_PROPERTIES.filter((property) => found.has(property)).map((property) => `${property}: ${found.get(property)};`);
  return kept.length ? kept.join(' ') : null;
}


/**
 * 🔒 A picture's width (T372; decision 1242): a whole percent of the column
 * from 10 to 100, and the four the picture bar names. A picture with none draws
 * at its own size, capped by the column.
 */
export const ARTICLE_PICTURE_WIDTH_MIN = 10;
export const ARTICLE_PICTURE_WIDTH_MAX = 100;
export const ARTICLE_PICTURE_SIZES = { small: 25, medium: 50, large: 75, full: 100 } as const;
export type ArticlePictureSize = keyof typeof ARTICLE_PICTURE_SIZES;

/** A typed or stored percent read as the picture keeps it, or null for anything else: a fraction, pixels, a bound crossed. */
export function articlePictureWidthOf(raw: string): number | null {
  const match = /^\s*(\d{1,3})\s*%?\s*$/.exec(raw);
  if (!match) return null;
  const percent = Number(match[1]);
  return percent >= ARTICLE_PICTURE_WIDTH_MIN && percent <= ARTICLE_PICTURE_WIDTH_MAX ? percent : null;
}

/**
 * 🔒 A picture's desired minimum width on screen (T1259; decision 1942,
 * amending 1476): whole pixels from 100 to 1200, kept as the picture's own
 * `--kb-min-width` declaration so the page's one rule reads it. A picture with
 * none takes the 200-pixel default, the `--article-picture-min-width` token.
 */
export const ARTICLE_PICTURE_MIN_WIDTH_PROPERTY = '--kb-min-width';
export const ARTICLE_PICTURE_MIN_WIDTH_LOW = 100;
export const ARTICLE_PICTURE_MIN_WIDTH_HIGH = 1200;
export const ARTICLE_PICTURE_MIN_WIDTH_DEFAULT = 200;

/** A typed or stored minimum read as the picture keeps it, or null for anything else: a fraction, a percent, a bound crossed. */
export function articlePictureMinWidthOf(raw: string): number | null {
  const match = /^\s*(\d{1,4})\s*(?:px)?\s*$/i.exec(raw);
  if (!match) return null;
  const pixels = Number(match[1]);
  return pixels >= ARTICLE_PICTURE_MIN_WIDTH_LOW && pixels <= ARTICLE_PICTURE_MIN_WIDTH_HIGH ? pixels : null;
}

/**
 * 🔨 A picture's style as the article keeps it (decisions 1242, 1942): its
 * width in percent and its minimum in pixels, in that order, and nothing else,
 * whatever wrote it. A value that fails its check drops that declaration alone.
 */
export function articlePictureStyleOf(style: string): string | null {
  let width: number | null = null;
  let minimum: number | null = null;
  let widthSeen = false;
  let minimumSeen = false;
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (property === 'width' && !widthSeen) {
      widthSeen = true;
      width = value.endsWith('%') ? articlePictureWidthOf(value) : null;
    }
    if (property === ARTICLE_PICTURE_MIN_WIDTH_PROPERTY && !minimumSeen) {
      minimumSeen = true;
      minimum = /px\s*$/i.test(value) ? articlePictureMinWidthOf(value) : null;
    }
  }
  const kept = [width === null ? null : `width: ${width}%;`, minimum === null ? null : `${ARTICLE_PICTURE_MIN_WIDTH_PROPERTY}: ${minimum}px;`].filter(Boolean);
  return kept.length ? kept.join(' ') : null;
}

/** 🔒 The letters a numbered list may count in (decision 1011): lower or upper case; a list of numbers writes none. */
export const ARTICLE_LIST_STYLES = ['a', 'A'] as const;

/** The article's allowance: its table, its style reader, a picture's id checked and its words kept short. */
export const ARTICLE_ALLOWANCE: RichTextAllowance = {
  tags: ARTICLE_RICH_TEXT_TAGS,
  // 🔒 A picture's style keeps its width alone; every other element's keeps the four declarations (decisions 0800, 1242).
  style: (value, element) => (element === 'img' ? articlePictureStyleOf(value) : articleStyleOf(value)),
  attribute: (name, value) => {
    if (name === 'data-kb-file') return UUID.test(value.trim()) ? value.trim().toLowerCase() : null;
    // 🔒 The alt text is kept as the caption is, one line cut at the same count on the words as typed (T759); an emptied one is dropped by the table as before.
    if (name === 'alt') return pictureLineKept(value);
    // 🔒 A caption is kept as the alt text is, one line of words, and an empty one is dropped (decision 1476).
    if (name === ARTICLE_PICTURE_CAPTION_ATTRIBUTE) return pictureLineKept(value) || null;
    if (name === 'start') return /^\d{1,4}$/.test(value.trim()) && Number(value) > 1 ? String(Number(value)) : null;
    if (name === 'type') return (ARTICLE_LIST_STYLES as readonly string[]).includes(value.trim()) ? value.trim() : null;
    // 🔒 A callout's kind is one of the three words, written in lower case; any other value drops the attribute, and the callout pass then drops the element (decision 10530).
    if (name === ARTICLE_CALLOUT_ATTRIBUTE) return articleCalloutKindOf(value);
    return value.trim();
  },
};

const PICTURE = /<img\b([^>]*)>/gi;
const PICTURE_ID = /\bdata-kb-file="([0-9a-f-]{36})"/i;
const PICTURE_ALT = /\balt="([^"]*)"/i;
const PICTURE_STYLE = /\bstyle="([^"]*)"/i;
const PICTURE_CAPTION = /\bdata-kb-caption="([^"]*)"/i;

/** A picture's kept caption, written back after its words and before its width (decision 1476). */
function pictureCaptionAttribute(attributes: string): string {
  const caption = PICTURE_CAPTION.exec(attributes)?.[1]?.trim();
  return caption ? ` ${ARTICLE_PICTURE_CAPTION_ATTRIBUTE}="${caption}"` : '';
}

/** A picture's kept width, written back after its id and its words (decision 1242). */
function pictureWidthAttribute(attributes: string): string {
  const style = PICTURE_STYLE.exec(attributes)?.[1];
  const kept = style ? articlePictureStyleOf(style) : null;
  return kept ? ` style="${kept}"` : '';
}

/** 🔨 A picture in its one stored form, its File's id, its words, its caption and its width, or nothing at all where it names no File. */
function canonicalPictures(safe: string): string {
  return safe.replace(PICTURE, (_whole, attributes: string) => {
    const id = PICTURE_ID.exec(attributes)?.[1];
    if (!id) return '';
    const alt = PICTURE_ALT.exec(attributes)?.[1];
    return `<img data-kb-file="${id.toLowerCase()}"${alt ? ` alt="${alt}"` : ''}${pictureCaptionAttribute(attributes)}${pictureWidthAttribute(attributes)}>`;
  });
}

/** Markup through the article's allowed table, every callout holding what a callout holds and every picture in its one stored form: what a read view runs on every render. */
export function sanitizeArticleMarkup(html: string): string {
  return canonicalPictures(articleCalloutsKept(sanitizeRichText(html, ARTICLE_ALLOWANCE)));
}

/**
 * The body made safe to store and to draw: the two markers kept in their one
 * stored form, the table of contents emptied, and every other part through the
 * article's allowed table.
 */
export function sanitizeArticleBody(html: string): string {
  if (!html) return '';
  const kept: string[] = [];
  const hold = (markup: string): string => {
    kept.push(markup);
    return `\uE001${kept.length - 1}\uE001`;
  };
  const held = html
    .replace(/\uE001/g, '')
    .replace(TOC_ANY, () => hold(TOC_PLACEHOLDER))
    // A same-length id that is not a UUID keeps its label and loses its link, rather than throwing out of a save (the KB1 review, 2026-09-25).
    .replace(FILE_LINK_ANY, (_whole, id: string, inner: string) => (UUID.test(id) ? hold(fileLinkHtml(id, labelOf(inner))) : escapeHtml(labelOf(inner))));
  // 🔨 The callout pass runs again once the two markers stand in place, so a callout holding the table of contents loses its element (decision 10530).
  return articleCalloutsKept(sanitizeArticleMarkup(held).replace(/\uE001(\d+)\uE001/g, (_whole, index: string) => kept[Number(index)] ?? ''));
}

/** Every File the words draw as a picture, once each, in the order they first appear. */
export function articlePictureIds(html: string): string[] {
  const ids: string[] = [];
  for (const match of html.matchAll(new RegExp(PICTURE.source, 'gi'))) {
    const id = PICTURE_ID.exec(match[1] ?? '')?.[1]?.toLowerCase();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Each picture given the address it is drawn from: the API's own picture route
 * on screen, the picture's bytes inline on paper. A picture with no address to
 * give is left out rather than drawn broken.
 */
export function articlePicturesResolved(safe: string, src: (fileId: string) => string | null): string {
  return safe.replace(PICTURE, (_whole, attributes: string) => {
    const id = PICTURE_ID.exec(attributes)?.[1]?.toLowerCase();
    const address = id ? src(id) : null;
    if (!id || !address) return '';
    const alt = PICTURE_ALT.exec(attributes)?.[1];
    return `<img src="${escapeHtml(address)}" data-kb-file="${id}"${alt ? ` alt="${alt}"` : ''}${pictureCaptionAttribute(attributes)}${pictureWidthAttribute(attributes)}>`;
  });
}

/**
 * 🔒 Each captioned picture with its caption drawn under it (T724; decision
 * 1476), for a reader and the PDF and never for the editor, whose words would
 * then keep the caption twice. The caption is a line of its own after the
 * picture, inside the picture's own line, so it takes that line's alignment.
 * The words are the attribute's, already escaped by the allowed table.
 */
export function articleCaptionsDrawn(safe: string): string {
  return safe.replace(PICTURE, (whole, attributes: string) => {
    const caption = PICTURE_CAPTION.exec(attributes)?.[1]?.trim();
    return caption ? `${whole}<span data-kb-caption-shown="true">${caption}</span>` : whole;
  });
}

/** 🔨 A read view's markup: the article's allowed table on every render, then each picture resolved to the address it is drawn from and its caption drawn under it. */
export function sanitizeArticleView(html: string, src: (fileId: string) => string | null): string {
  return articleCaptionsDrawn(articlePicturesResolved(sanitizeArticleMarkup(html), src));
}

/** One H1 or H2 of the article, in document order, as the table of contents lists it. */
export interface ArticleHeading {
  level: 1 | 2;
  text: string;
}

/** The article's own H1 and H2 nodes, read at render time. */
export function articleHeadings(html: string): ArticleHeading[] {
  const out: ArticleHeading[] = [];
  for (const match of html.matchAll(/<h([12])\b[^>]*>([\s\S]*?)<\/h\1\s*>/gi)) {
    const text = labelOf(match[2]!);
    if (text) out.push({ level: Number(match[1]) as 1 | 2, text });
  }
  return out;
}

/** Whether the body carries the table of contents marker. */
export function hasTableOfContents(html: string): boolean {
  return new RegExp(TOC_ANY.source, 'i').test(html);
}

/** Every File the words link to, once each, in the order they first appear. */
export function articleFileIds(html: string): string[] {
  const ids: string[] = [];
  for (const match of html.matchAll(new RegExp(FILE_LINK_ANY.source, 'gi'))) {
    const id = match[1]!.toLowerCase();
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** A part of the body as a renderer draws it: rich text, or the place the table of contents goes. */
export type ArticleSegment = { kind: 'html'; html: string } | { kind: 'toc' };

/**
 * The body in the parts a page or a sheet draws: the sanitised markup with every
 * file link turned into a link to that File's own address, split where the
 * table of contents stands. 🔨 The same function feeds the screen and the PDF,
 * so nothing is converted with loss at export time.
 */
export function articleSegments(html: string, fileHref: (fileId: string) => string): ArticleSegment[] {
  const safe = sanitizeArticleBody(html).replace(new RegExp(FILE_LINK_ANY.source, 'gi'), (_whole, id: string, inner: string) => {
    const href = escapeHtml(fileHref(id.toLowerCase()));
    return `<a href="${href}" data-kb-file="${id.toLowerCase()}" rel="noopener noreferrer" target="_blank">${inner}</a>`;
  });
  const parts = safe.split(TOC_PLACEHOLDER);
  const out: ArticleSegment[] = [];
  parts.forEach((part, index) => {
    if (index > 0) out.push({ kind: 'toc' });
    if (part) out.push({ kind: 'html', html: part });
  });
  return out;
}

/**
 * The table of contents as the editor draws it inside the marker, so the
 * author sees the entries where they will be read. The save empties it again.
 */
export function tocEditorHtml(headings: readonly ArticleHeading[], title: string): string {
  const entries = headings.map((heading) => `<li data-kb-toc-level="${heading.level}">${escapeHtml(heading.text)}</li>`).join('');
  return `<nav data-kb-toc="true" contenteditable="false"><p><b>${escapeHtml(title)}</b></p><ul>${entries}</ul></nav>`;
}

/** One part of an article as the phone draws it (decision 9580): a run of words, or a picture on a line of its own. */
export type ArticleReadingBlock = { kind: 'words'; html: string } | { kind: 'picture'; fileId: string; alt: string; caption: string };

const TABLE_ROW = /<tr\b[^>]*>([\s\S]*?)<\/tr\s*>/gi;
const TABLE_CELL = /<t[dh]\b[^>]*>([\s\S]*?)<\/t[dh]\s*>/gi;
const CELL_BLOCK = /<\/?(?:p|div|h[1-6]|blockquote|ul|ol|li|pre)\b[^>]*>|<br\s*\/?>/gi;

/** A picture's alt text or caption as typed, its entities read back as characters. */
function pictureWords(value: string | undefined): string {
  return (value ?? '').replace(CAPTION_ENTITY, captionCharacter).replace(/\s+/g, ' ').trim();
}

/**
 * 🔒 An article's body in the parts the phone draws (decision 9580): the words, which the phone's rich-note renderer
 * draws under the notes' allowance, and each picture by its File's id on a line of its own. 🔨 The phone holds no
 * table, so a table reads one paragraph a row, its cells joined by a dot. A rule is dropped, a file link keeps its
 * words and no address, and the table of contents is not drawn, because the phone's page has nowhere to jump.
 * 🔨 What it drops is named in piece 30 (T3449): font sizes, a lettered list's type, h5 and h6 as headings, a picture with no valid
 * id, and a multi-line code block's line breaks.
 */
export function articleReadingBlocks(html: string): ArticleReadingBlock[] {
  const safe = sanitizeArticleBody(html)
    .split(TOC_PLACEHOLDER)
    .join('')
    .replace(new RegExp(FILE_LINK_ANY.source, 'gi'), (_whole, _id: string, inner: string) => inner)
    .replace(TABLE_ROW, (_whole, row: string) => {
      // 🔨 A picture in a cell stands on a line of its own after the row's words, so the row's paragraph is never split around it (T3449).
      const pictures: string[] = [];
      const cells = [...row.matchAll(new RegExp(TABLE_CELL.source, 'gi'))]
        .map((cell) => (cell[1] ?? '').replace(new RegExp(PICTURE.source, 'gi'), (picture) => { pictures.push(picture); return ''; }).replace(CELL_BLOCK, ' ').replace(/\s+/g, ' ').trim())
        .filter((cell) => cell !== '');
      return `${cells.length ? `<p>${cells.join(' · ')}</p>` : ''}${pictures.join('')}`;
    })
    .replace(/<\/?(?:table|thead|tbody|tr|td|th)\b[^>]*>/gi, '')
    .replace(/<hr\s*\/?>/gi, '');
  const blocks: ArticleReadingBlock[] = [];
  const words = (part: string) => {
    if (richTextPlain(part).trim() !== '') blocks.push({ kind: 'words', html: part });
  };
  let at = 0;
  for (const match of safe.matchAll(new RegExp(PICTURE.source, 'gi'))) {
    words(safe.slice(at, match.index));
    const attributes = match[1] ?? '';
    const fileId = PICTURE_ID.exec(attributes)?.[1]?.toLowerCase();
    if (fileId) blocks.push({ kind: 'picture', fileId, alt: pictureWords(PICTURE_ALT.exec(attributes)?.[1]), caption: pictureWords(PICTURE_CAPTION.exec(attributes)?.[1]) });
    at = (match.index ?? 0) + match[0].length;
  }
  words(safe.slice(at));
  return blocks;
}

/** A ranked passage split into its plain runs and its matches, the `[[` and `]]` the index writes around each hit. */
export function passageParts(passage: string): { text: string; hit: boolean }[] {
  return passage
    .split(/(\[\[.*?\]\])/g)
    .filter((part) => part !== '')
    .map((part) => (part.startsWith('[[') && part.endsWith(']]') ? { text: part.slice(2, -2), hit: true } : { text: part, hit: false }));
}

/** The words the search index reads: the body's text with the table of contents left out. */
export function articlePlainText(html: string): string {
  // 🔒 Every kept element's words are indexed, a table's cells included (decision 0800): a cell, a row, a rule, a code block and a callout each end a run of words; a callout's drawn word is not in the body and is not indexed (decision 10530).
  const separated = sanitizeArticleBody(html).split(TOC_PLACEHOLDER).join('\n').replace(/<\/(td|th|tr|pre|aside)\s*>|<hr>/gi, (tag) => `\n${tag}`);
  return richTextPlain(separated);
}
